import React, { useState, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import NotificationsActions from '~redux/notifications';
import PromptService from '~services/prompt';
import { useStateFromProp } from '~hooks/common';

import { AuthContext } from '~contexts/auth';
import { UserContext } from '~contexts/user';

import Radio from '~components/forms/radio';
import Button from '~components/button';
import Modal from '~components/modal';
import './style.scss';

const UnsubscribePopup = ({ isOpened, onToggle = () => {} }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { user, updateWorkerParam, updateCustomerParam, deleteUser, deleteUserPicture } = useContext(UserContext);
  const { signOut } = useContext(AuthContext);
  const { personId, accountType, wish, operations } = user;

  const [isModalOpened, setModalOpen] = useStateFromProp(isOpened, onToggle);
  const [accountAction, setAccountAction] = useState('pause-advertisement');
  const [isSaving, setIsSaving] = useState(false);

  const deleteAccount = async () => {
    try {
      await Promise.all([deleteUserPicture(personId), deleteUser(personId)]);
      dispatch(
        NotificationsActions.addNotification(
          intl.formatMessage({
            id: 'containers.account.actions.delete-notification',
            defaultMessage: 'You account has been deleted',
          })
        )
      );
      signOut();
    } catch (err) {
      PromptService.error(err);
    }
  };

  const pauseAdvertisement = async () => {
    try {
      if (accountType === 'worker') {
        const newUser = { ...user, operations: { ...operations, published: false } };
        await updateWorkerParam(personId, { published: false }, newUser);
      } else {
        const newUser = { ...user, wish: { ...wish, published: false } };
        await updateCustomerParam(personId, { published: false }, newUser);
      }

      dispatch(
        NotificationsActions.addNotification(
          intl.formatMessage({
            id: 'containers.account.actions.suspend-notification',
            defaultMessage: 'You offer has been suspended',
          })
        )
      );
    } catch (err) {
      PromptService.error(err);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    if (accountAction === 'close-account') {
      await deleteAccount();
    } else {
      await pauseAdvertisement();
    }
    setIsSaving(false);
    onToggle(false);
  };

  return (
    <Modal.Wrapper isOpened={isModalOpened} onClose={() => setModalOpen()} className="c-modal--unsubscribe-popup">
      <Modal.Header>
        <h3 className="l-headline--tertiary">
          {intl.formatMessage({
            id: 'containers.account.unsubscribe.popup.headline',
            defaultMessage: 'Would you also like to pause your advertisement?',
          })}
        </h3>
      </Modal.Header>
      <Modal.Content>
        <div className="c-unsubscribe-popup">
          <Radio.Container
            value={accountAction}
            onChange={setAccountAction}
            className="c-radio__container--horizontal c-unsubscribe-checkboxes"
          >
            <Radio.Option value="pause-advertisement" className="c-radio--bordered c-radio--with-children">
              <div className="c-radio__children">
                <div className="c-radio__title">
                  {intl.formatMessage({
                    id: 'containers.unsubscribe.account-actions.pause-advertisement.title',
                  })}
                </div>
                <div className="c-radio__content">
                  {intl.formatMessage({
                    id: 'containers.unsubscribe.account-actions.pause-advertisement.content',
                  })}
                </div>
              </div>
            </Radio.Option>
            <Radio.Option value="close-account" className="c-radio--bordered c-radio--with-children">
              <div className="c-radio__children">
                <div className="c-radio__title">
                  {intl.formatMessage({
                    id: 'containers.unsubscribe.account-actions.close-account.title',
                  })}
                </div>
                <div className="c-radio__content">
                  {intl.formatMessage({
                    id: 'containers.unsubscribe.account-actions.close-account.content',
                  })}
                </div>
              </div>
            </Radio.Option>
          </Radio.Container>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className="c-modal__buttons--flex-end">
          <Button className="c-btn--secondary c-modal__button" onClick={() => onToggle(false)}>
            {intl.formatMessage({
              id: 'containers.account.unsubscribe.popup.close',
              defaultMessage: 'Close',
            })}
          </Button>
          <Button className="c-btn c-modal__button" onClick={handleSave} isLoading={isSaving}>
            {intl.formatMessage({
              id: 'containers.account.unsubscribe.popup.save',
              defaultMessage: 'Save',
            })}
          </Button>
        </div>
      </Modal.Footer>
    </Modal.Wrapper>
  );
};

export default UnsubscribePopup;
